'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  getTourCardPosition,
  readTourAnchorRect,
  type TourAnchorRect,
} from '../lib/geometry';
import {
  PRODUCT_TOUR_GLOW_RING_CLASS,
  productTourGlowStyles,
  useTourAnchorGlow,
} from '../lib/useTourAnchorGlow';
import type { ProductTourStep } from '../lib/types';
import { ProductTourSpotlight } from './ProductTourSpotlight';

export type ProductTourOverlayRenderCardProps = {
  step: ProductTourStep;
  stepIndex: number;
  totalSteps: number;
  position: ReturnType<typeof getTourCardPosition>;
  waitsForAction: boolean;
  showNext: boolean;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
};

type ProductTourOverlayProps = {
  mounted: boolean;
  isOpen: boolean;
  step: ProductTourStep | undefined;
  stepIndex: number;
  totalSteps: number;
  actionAlreadySatisfied: boolean;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
  onRestart: () => void;
  renderCard: (props: ProductTourOverlayRenderCardProps) => ReactNode;
  renderCollapsedRestart?: (props: { onRestart: () => void }) => ReactNode;
  zIndex?: number;
};

export function ProductTourOverlay({
  mounted,
  isOpen,
  step,
  stepIndex,
  totalSteps,
  actionAlreadySatisfied,
  onSkip,
  onBack,
  onNext,
  onRestart,
  renderCard,
  renderCollapsedRestart,
  zIndex = 1400,
}: ProductTourOverlayProps) {
  const [anchorRect, setAnchorRect] = useState<TourAnchorRect | null>(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useTourAnchorGlow({
    isActive: isOpen && !!step?.anchorId,
    anchorId: step?.anchorId,
  });

  useEffect(() => {
    if (!mounted) {
      return;
    }
    setViewport({ width: window.innerWidth, height: window.innerHeight });
  }, [mounted]);

  useEffect(() => {
    if (!isOpen || !step) {
      return undefined;
    }

    const update = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      setAnchorRect(readTourAnchorRect(step.anchorId));
    };

    update();
    const interval = setInterval(update, 200);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, step]);

  const cardPosition = useMemo(() => {
    if (!step) {
      return { top: 24, left: 16, placement: 'center' as const };
    }
    return getTourCardPosition({
      presentation: step.presentation,
      anchorRect,
      viewport,
      cardPlacement: step.cardPlacement,
    });
  }, [anchorRect, step, viewport]);

  const glowStyles = useMemo(() => productTourGlowStyles(), []);

  if (!mounted || !step) {
    return null;
  }

  if (!isOpen) {
    if (!renderCollapsedRestart) {
      return null;
    }
    return createPortal(
      <div
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex,
        }}
      >
        {renderCollapsedRestart({ onRestart })}
      </div>,
      document.body,
    );
  }

  const waitsForAction =
    step.advance.type !== 'manual' && !actionAlreadySatisfied;
  const showNext = !waitsForAction || !anchorRect || actionAlreadySatisfied;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        pointerEvents: 'none',
      }}
    >
      <style>{glowStyles}</style>

      {step.presentation !== 'center' && anchorRect && (
        <div
          className={PRODUCT_TOUR_GLOW_RING_CLASS}
          style={{
            top: anchorRect.top - 2,
            left: anchorRect.left - 2,
            width: anchorRect.width + 4,
            height: anchorRect.height + 4,
            zIndex: zIndex + 5,
          }}
        />
      )}

      <ProductTourSpotlight
        presentation={step.presentation}
        waitsForAction={waitsForAction}
        onDismissBackdrop={waitsForAction ? undefined : onNext}
      />

      {renderCard({
        step,
        stepIndex,
        totalSteps,
        position: cardPosition,
        waitsForAction,
        showNext,
        onSkip,
        onBack,
        onNext,
      })}
    </div>,
    document.body,
  );
}
